"use client";

import { useState, useTransition } from "react";
import {
  Alert,
  Button,
  Card,
  Input,
  Label,
  TextArea,
  TextField,
} from "@heroui/react";
import { Envelope } from "@phosphor-icons/react";

import { submitSupportRequestAction } from "@/app/(marketing)/support/actions";
import { GRIEVANCE_EMAIL } from "@/lib/support-contact";

/**
 * The support form.
 *
 * Replaces a `mailto:` link. That link sent every message into an inbox, where
 * it had no due date, no queue position and no record of having been answered
 * — so messages were worked out of a mail client and sometimes not at all.
 * This files into the same queue the privacy centre uses, which means the
 * operator sees it in the console with a deadline against it.
 *
 * The address stays published underneath. Someone whose problem is that the
 * site is broken should not have to use the site to tell us.
 */
export function SupportForm({ defaultEmail }: { defaultEmail?: string }) {
  const [email, setEmail] = useState(defaultEmail ?? "");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<{ reference: string; dueBy: string } | null>(null);
  const [isPending, startTransition] = useTransition();

  function submit() {
    setError(null);

    startTransition(async () => {
      const result = await submitSupportRequestAction({ email, subject, message });

      if (!result.ok) {
        setError(result.error ?? "That could not be sent.");
        return;
      }

      setSent({ reference: result.reference!, dueBy: result.dueBy! });
      setSubject("");
      setMessage("");
    });
  }

  if (sent) {
    return (
      <Card className="p-6">
        <Card.Content className="flex flex-col gap-3 p-0">
          <Alert status="success">
            <Alert.Content>
              <Alert.Title>We have your message</Alert.Title>
              <Alert.Description>
                Your reference is <strong>{sent.reference}</strong>. We will
                reply to {email} by {sent.dueBy}. If you have an account, you
                can also follow it in your privacy centre.
              </Alert.Description>
            </Alert.Content>
          </Alert>

          <Button
            size="sm"
            variant="ghost"
            className="self-start"
            onPress={() => setSent(null)}
          >
            Send another message
          </Button>
        </Card.Content>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <Card.Content className="flex flex-col gap-4 p-0">
        <Envelope weight="duotone" className="text-muted size-6" />
        <h2 className="text-xl font-semibold tracking-tight">Still stuck?</h2>
        <p className="text-muted max-w-xl text-sm leading-relaxed">
          Send it here rather than by email — it goes straight into our queue
          with a reference and a reply date, so it cannot get lost. If it is
          about a specific job, include its id from the History page.
        </p>

        {error ? (
          <Alert status="danger">
            <Alert.Content>
              <Alert.Description>{error}</Alert.Description>
            </Alert.Content>
          </Alert>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            value={email}
            onChange={setEmail}
            isDisabled={isPending}
            type="email"
            autoComplete="email"
          >
            <Label>Your email</Label>
            <Input placeholder="you@company.com" />
          </TextField>

          <TextField value={subject} onChange={setSubject} isDisabled={isPending}>
            <Label>Subject</Label>
            <Input placeholder="Upscale came out blurry" />
          </TextField>
        </div>

        <TextField value={message} onChange={setMessage} isDisabled={isPending}>
          <Label>What is going on?</Label>
          <TextArea
            rows={5}
            placeholder="Describe the problem, and include a job id if it is about one."
          />
        </TextField>

        <Button
          variant="primary"
          size="sm"
          className="self-start"
          isDisabled={isPending || !email || subject.trim().length < 3 || message.trim().length < 20}
          onPress={submit}
        >
          {isPending ? "Sending…" : "Send message"}
        </Button>

        <p className="text-muted text-xs">
          Prefer email?{" "}
          <a
            href={`mailto:${GRIEVANCE_EMAIL}`}
            className="underline underline-offset-4"
          >
            {GRIEVANCE_EMAIL}
          </a>{" "}
          still reaches us — it just takes us longer to spot.
        </p>
      </Card.Content>
    </Card>
  );
}
